import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { genreOptions, statusOptions, styleTags, sortOptions } from "@/mocks/categories";
import { filterComics, type FilterState } from "@/utils/comic";
import ComicCard from "@/components/comic/ComicCard";
import SkeletonCard from "@/components/comic/SkeletonCard";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

const initialFilters: FilterState = {
  genre: "全部",
  status: "全部",
  tag: "全部",
  sort: "updated",
  keyword: "",
};

function FilterGroup({
  title,
  options,
  active,
  onSelect,
}: {
  title: string;
  options: string[];
  active: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="mb-6">
      <h3 className="mb-3 text-sm font-semibold text-foreground-950">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              active === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const genre = searchParams.get("genre") ?? "全部";
    const status = searchParams.get("status") ?? "全部";
    const tag = searchParams.get("tag") ?? "全部";
    const sort = searchParams.get("sort") ?? "updated";
    const keyword = searchParams.get("q") ?? "";
    setFilters({ genre, status, tag, sort, keyword });
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    setVisible(PAGE_SIZE);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useSeo({
    title: `${filters.genre === "全部" ? "漫画分类" : filters.genre} - 韩漫免费漫画在线观看阅读双女主`,
    description: `浏览${filters.genre === "全部" ? "全部" : filters.genre}题材的双女主韩漫，支持按题材、连载状态与风格标签筛选，免费在线阅读最新章节。`,
    keywords: "韩漫分类,双女主漫画,免费漫画在线观看,韩漫筛选,连载中漫画",
    canonicalPath: "/category",
  });

  const results = useMemo(() => filterComics(comics, filters), [filters]);
  const shown = results.slice(0, visible);
  const hasMore = visible < results.length;

  const update = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    const next = { ...filters, ...patch };
    const params = new URLSearchParams();
    if (next.genre !== "全部") params.set("genre", next.genre);
    if (next.status !== "全部") params.set("status", next.status);
    if (next.tag !== "全部") params.set("tag", next.tag);
    if (next.sort !== "updated") params.set("sort", next.sort);
    setSearchParams(params);
  };

  const reset = () => {
    setFilters(initialFilters);
    setSearchParams(new URLSearchParams());
  };

  const currentSummary = [
    filters.genre !== "全部" ? `题材：${filters.genre}` : null,
    filters.status !== "全部" ? `状态：${filters.status}` : null,
    filters.tag !== "全部" ? `风格：${filters.tag}` : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
      <nav className="mb-6 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">漫画分类</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">漫画分类</h1>
        <p className="mt-2 text-sm text-foreground-600">
          按题材、连载状态与风格标签多维度筛选双女主韩漫，共 {results.length} 部作品
          {currentSummary.length > 0 && <span className="ml-1 text-foreground-500">（{currentSummary.join("，")}）</span>}
        </p>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="lg:hidden">
          <button
            type="button"
            onClick={() => setFilterOpen((prev) => !prev)}
            className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-900"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line" /> 筛选条件
            </span>
            <i className={filterOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"} />
          </button>
        </div>

        <aside className={`w-full shrink-0 lg:block lg:w-64 ${filterOpen ? "block" : "hidden"}`}>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选</h2>
              <button
                type="button"
                onClick={reset}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-refresh-line" /> 重置筛选
              </button>
            </div>

            <FilterGroup
              title="题材"
              options={["全部", ...genreOptions]}
              active={filters.genre}
              onSelect={(value) => update({ genre: value })}
            />
            <FilterGroup
              title="连载状态"
              options={statusOptions}
              active={filters.status}
              onSelect={(value) => update({ status: value })}
            />
            <FilterGroup
              title="风格标签"
              options={["全部", ...styleTags]}
              active={filters.tag}
              onSelect={(value) => update({ tag: value })}
            />

            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground-950">排序方式</h3>
              <div className="flex flex-col gap-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => update({ sort: option.key })}
                    className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                      filters.sort === option.key
                        ? "bg-primary-100 text-primary-700"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {option.label}
                    {filters.sort === option.key && <i className="ri-check-line" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
              <p className="mt-4 text-sm text-foreground-600">暂无符合条件的漫画</p>
              <button
                type="button"
                onClick={reset}
                className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4" data-product-shop>
                {shown.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    加载更多（剩余 {results.length - visible} 部）
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}